import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import {
  ApiGetBriefIconList,
  ApiAddBriefIcon,
  ApiUpdateBriefIcon,
  ApiuploadDocuments
} from '../../../apiService/Core/Apicall.tsx';
import { setLoadingFalse, setLoadingTrue } from '../../../global/config/Action/loadingActions.ts';
import store from '../../../global/config/store.ts';
import uploadPlaceholder from '../../../assets/images/upload.png';

interface BriefIcon {
  briefIconId: number;
  iconName: string;
  iconImage: string;
  isActive: boolean;
  createdAt?: string;
  createdDate?: string;
}

const BriefIconList: React.FC = () => {
  const [icons, setIcons] = useState<BriefIcon[]>([]);
  const [search, setSearch] = useState<string>('');
  const [filteredIcons, setFilteredIcons] = useState<BriefIcon[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedIcon, setSelectedIcon] = useState<BriefIcon | null>(null);

  const fetchIcons = async () => {
    try {
      const response = await ApiGetBriefIconList('?isActive=true');
      const data = response?.data || response || [];
      setIcons(data);
      setTotalCount(data.length);
    } catch (error: any) {
      console.error('Error fetching brief icons:', error);
      toast.error(error || 'Failed to fetch brief icons');
    }
  };

  useEffect(() => {
    fetchIcons();
  }, []);

  useEffect(() => {
    if (icons) {
      const filtered = icons.filter((icon) =>
        icon.iconName?.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredIcons(filtered);
    }
  }, [search, icons]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = async (id: number) => {
    try {
      store.dispatch(setLoadingTrue());
      const response = await ApiGetBriefIconList(`?briefIconId=${id}&isActive=true`);
      const iconDetails = response?.data?.[0] || response?.data || response;
      
      if (iconDetails) {
        setSelectedIcon(iconDetails);
        setIsEditMode(true);
        setOpenDialog(true);
      } else {
        toast.error('Could not fetch Brief Icon details');
      }
    } catch (error: any) {
      toast.error(error || 'Failed to fetch Brief Icon details');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  const handleDeleteClick = async (id: number) => {
    const isConfirmed = window.confirm('Are you sure you want to delete this Brief Icon?');
    if (isConfirmed) {
      try {
        const payload = {
          briefIconId: id,
          isActive: false
        };
        const response = await ApiUpdateBriefIcon(payload);
        toast.success('Brief Icon deleted successfully');
        fetchIcons();
      } catch (error: any) {
        toast.error(error || 'Failed to delete Brief Icon');
      }
    }
  };

  const handleOpenAddDialog = () => {
    setSelectedIcon(null);
    setIsEditMode(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    formik.resetForm();
  };

  // Formik & Yup setup
  const validationSchema = Yup.object({
    iconName: Yup.string().trim().required('Icon Name is required'),
    iconImage: Yup.string().required('Icon Image is required')
  });

  const onSubmit = async (values: { iconName: string; iconImage: string }) => {
    try {
      if (isEditMode && selectedIcon) {
        const payload = {
          briefIconId: selectedIcon.briefIconId,
          iconName: values.iconName.trim(),
          iconImage: values.iconImage,
          isActive: true
        };
        const response = await ApiUpdateBriefIcon(payload);
        toast.success(response.message || 'Brief Icon updated successfully');
      } else {
        const payload = {
          iconName: values.iconName.trim(),
          iconImage: values.iconImage
        };
        const response = await ApiAddBriefIcon(payload);
        toast.success(response.message || 'Brief Icon added successfully');
      }
      handleCloseDialog();
      fetchIcons();
    } catch (error: any) {
      toast.error(error || 'Failed to save Brief Icon');
    }
  };

  const formik = useFormik({
    initialValues: {
      iconName: selectedIcon?.iconName || '',
      iconImage: selectedIcon?.iconImage || ''
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit
  });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      try {
        store.dispatch(setLoadingTrue());
        const options = {
          maxSizeMB: 5,
          maxWidthOrHeight: 800,
          useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);

        const formData = new FormData();
        formData.append('fileType', 'brief_icon');
        formData.append('file', compressedFile);

        const response: any = await ApiuploadDocuments(formData);
        if (response.status) {
          formik.setFieldValue('iconImage', response.location);
          toast.success('Image uploaded successfully');
        } else {
          toast.error('Image upload failed');
        }
      } catch (error: any) {
        console.error('Image upload failed:', error);
        toast.error(error?.message || 'Error uploading image');
      } finally {
        store.dispatch(setLoadingFalse());
      }
    } else if (file) {
      toast.error('Please upload a valid image file');
    }
  };

  const handleRemoveImage = () => {
    formik.setFieldValue('iconImage', '');
  };

  // Paginated View
  const paginatedIcons = filteredIcons.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box className="container-fluid p-3 extra" bgcolor="white" minHeight="calc(100vh - 180px)">
      <Box className="d-flex mb-3 align-items-center flex-wrap justify-content-between" gap={2}>
        <Typography className="fw-bold col-12 col-md-auto" textAlign={{ xs: 'center', md: 'start' }} variant="h4">
          Brief Icons
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpenAddDialog}
          className="table-row-background link-btn"
          sx={{ textTransform: 'none' }}
        >
          Add Brief Icon
        </Button>
      </Box>

      <FormControl className="my-3 mt-4 col-12">
        <TextField
          label="Search Brief Icon"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
        />
      </FormControl>

      <Box className="w-100" overflow="auto">
        <Table>
          <TableHead>
            <TableRow className="table-row-background">
              {['Icon Preview', 'Icon Name', 'Created Date', 'Actions'].map((header) => (
                <TableCell key={header} className="tableCell-header" sx={{ color: 'white' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedIcons.length > 0 ? (
              paginatedIcons.map((icon) => (
                <TableRow key={icon.briefIconId}>
                  <TableCell>
                    {icon.iconImage ? (
                      <Box
                        component="img"
                        src={icon.iconImage}
                        alt={icon.iconName}
                        sx={{
                          width: 48,
                          height: 48,
                          objectFit: 'contain',
                          border: '1px solid #0000001A',
                          borderRadius: '5px'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: '#f0f0f0',
                          border: '1px solid #0000001A',
                          borderRadius: '5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          color: 'text.secondary'
                        }}
                      >
                        No Image
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{icon.iconName}</TableCell>
                  <TableCell>
                    {icon.createdDate || icon.createdAt
                      ? new Date(icon.createdDate || icon.createdAt || '').toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton onClick={() => handleEditClick(icon.briefIconId)} size="small" color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteClick(icon.briefIconId)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                    No Brief Icons found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        className="w-100"
        count={filteredIcons.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            {isEditMode ? 'Edit Brief Icon' : 'Add Brief Icon'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="iconName"
                  name="iconName"
                  label="Icon Name"
                  size="small"
                  value={formik.values.iconName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.iconName && Boolean(formik.errors.iconName)}
                  helperText={formik.touched.iconName && formik.errors.iconName}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Icon Image Upload
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed #0000001A',
                    borderRadius: '10px',
                    p: 3,
                    textAlign: 'center',
                    bgcolor: '#fafafa',
                    position: 'relative'
                  }}
                >
                  {formik.values.iconImage ? (
                    <Box>
                      <Box
                        component="img"
                        src={formik.values.iconImage}
                        alt="Uploaded preview"
                        sx={{
                          maxHeight: 120,
                          maxWidth: '100%',
                          objectFit: 'contain',
                          mb: 2
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleRemoveImage}
                        sx={{ textTransform: 'none', display: 'block', mx: 'auto' }}
                      >
                        Remove Image
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="icon-image-upload"
                        type="file"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="icon-image-upload">
                        <Box
                          component="img"
                          src={uploadPlaceholder}
                          alt="Upload placeholder"
                          sx={{ width: 48, height: 48, cursor: 'pointer', mb: 1 }}
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ cursor: 'pointer' }}>
                          Click here to upload image
                        </Typography>
                      </label>
                    </Box>
                  )}
                </Box>
                {formik.touched.iconImage && formik.errors.iconImage && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.iconImage}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              sx={{ color: '#75724C', borderColor: '#75724C', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="table-row-background"
              sx={{ textTransform: 'none' }}
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default BriefIconList;
